<template>
  <div>
    <div
      class="mb-1 font-secondary font-medium text-[14px] leading-[22px] text-gray-800"
    >
      Page 1
    </div>

    <MatchTagSummary
      :entries="tagEntries"
      :selected-number="selectedNumber"
      @select="selectMatch"
    />

    <div class="border border-gray-400/25 rounded-[3px] overflow-hidden">
      <div class="relative">
        <img
          ref="imageRef"
          :src="imageUrl"
          class="w-full pointer-events-none block transition-opacity"
          :class="{ 'opacity-30': selectedNumber !== null }"
          :alt="fileName"
          @load="onImageLoad"
        />

        <img
          v-if="cutoutStyle"
          :src="imageUrl"
          class="absolute top-0 left-0 w-full pointer-events-none block"
          :style="cutoutStyle"
          :alt="fileName"
        />

        <div
          v-for="box in visibleBoxes"
          :key="`box-${box.key}`"
          class="absolute rounded-sm cursor-pointer transition-colors"
          :class="boxStateClass(box)"
          :style="{
            top: `${box.top}%`,
            left: `${box.left}%`,
            width: `${box.width}%`,
            height: `${box.height}%`,
          }"
          @mouseenter="hoveredNumber = box.number"
          @mouseleave="hoveredNumber = null"
          @click.stop="selectMatch(box.number)"
        />

        <MatchTag
          v-for="marker in visibleMarkers"
          :key="marker.key"
          :ref="(el) => setMarkerRef(marker.number, el)"
          :id="marker.number"
          :level="marker.level"
          size="lg"
          clickable
          class="absolute -translate-x-2 -translate-y-2 z-10"
          :style="{ top: `${marker.top}%`, left: `${marker.left}%` }"
          @mouseenter="hoveredNumber = marker.number"
          @mouseleave="hoveredNumber = null"
          @click="selectMatch(marker.number)"
        />
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="selectedMatch && tooltipStyle"
        :style="tooltipStyle"
        class="pointer-events-auto"
        @click.stop
      >
        <MatchTooltip :match="selectedMatch" @view="onView" />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import MatchTagSummary from './MatchTagSummary.vue'
import MatchTag from './MatchTag.vue'
import MatchTooltip from '../MatchTooltip.vue'
import { ScoreLevel, type Marker } from '@/shared/graphql/router/graphql'
import type { TimelineMatch } from '../timeline-types'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'

export interface ImageMatchInput {
  id: string
  level?: ScoreLevel | null
  location?: string | null
  markers?: Array<Marker | null> | null
  bbox?: string[] | null
}

const props = defineProps<{
  imageUrl: string
  fileName?: string
  matches: ImageMatchInput[]
  timelineMatchById?: ReadonlyMap<string, TimelineMatch>
}>()

const emit = defineEmits<{
  view: [matchId: string]
}>()

const imageRef = ref<HTMLImageElement | null>(null)
const imageWidth = ref(0)
const imageHeight = ref(0)
const selectedNumber = ref<number | null>(null)
const hoveredNumber = ref<number | null>(null)

const tagEntries = computed(() =>
  props.matches.map((match, index) => ({
    number: index + 1,
    level: match.level,
  }))
)

const onImageLoad = () => {
  if (!imageRef.value) return
  imageWidth.value = imageRef.value.naturalWidth
  imageHeight.value = imageRef.value.naturalHeight
}

const parseBbox = (bbox?: string[] | null) => {
  if (!bbox || bbox.length < 4) return null
  const [x1, y1, x2, y2] = bbox.map(parseFloat)
  if ([x1, y1, x2, y2].some(Number.isNaN)) return null
  return { x1, y1, x2, y2 }
}

const boxes = computed(() => {
  if (!imageWidth.value || !imageHeight.value) return []

  return props.matches
    .map((match, index) => {
      const rect = parseBbox(match.bbox)
      if (!rect) return null
      return {
        key: match.id,
        number: index + 1,
        level: match.level,
        top: (rect.y1 / imageHeight.value) * 100,
        left: (rect.x1 / imageWidth.value) * 100,
        width: ((rect.x2 - rect.x1) / imageWidth.value) * 100,
        height: ((rect.y2 - rect.y1) / imageHeight.value) * 100,
      }
    })
    .filter((b): b is NonNullable<typeof b> => b !== null)
})

const markers = computed(() =>
  boxes.value.map((box, index) => ({
    key: box.key,
    number: box.number || index + 1,
    level: box.level,
    top: box.top,
    left: box.left,
  }))
)

const selectMatch = (number: number) => {
  selectedNumber.value = selectedNumber.value === number ? null : number
}

const visibleBoxes = computed(() =>
  selectedNumber.value !== null
    ? boxes.value.filter((b) => b.number === selectedNumber.value)
    : boxes.value
)

const visibleMarkers = computed(() =>
  selectedNumber.value !== null
    ? markers.value.filter((m) => m.number === selectedNumber.value)
    : markers.value
)

const cutoutStyle = computed(() => {
  if (selectedNumber.value === null) return null
  const box = boxes.value.find((b) => b.number === selectedNumber.value)
  if (!box) return null
  const top = box.top
  const right = 100 - (box.left + box.width)
  const bottom = 100 - (box.top + box.height)
  const left = box.left
  return {
    clipPath: `inset(${top}% ${right}% ${bottom}% ${left}%)`,
  }
})

const boxStateClass = (box: { number: number; level?: ScoreLevel | null }) => {
  const isSelected = selectedNumber.value === box.number
  const isHovered = hoveredNumber.value === box.number

  if (isSelected) {
    return {
      'border-2': true,
      'border-gray-500 bg-gray-500/10': !box.level,
      'border-red-500 bg-red-500/10': box.level === ScoreLevel.High,
      'border-yellow-500 bg-yellow-500/10': box.level === ScoreLevel.Medium,
      'border-green-500 bg-green-500/10': box.level === ScoreLevel.Low,
    }
  }

  if (isHovered) {
    return {
      'bg-gray-500/25': !box.level,
      'bg-red-500/25': box.level === ScoreLevel.High,
      'bg-yellow-500/25': box.level === ScoreLevel.Medium,
      'bg-green-500/25': box.level === ScoreLevel.Low,
    }
  }

  return {
    'bg-gray-500/10': !box.level,
    'bg-red-500/10': box.level === ScoreLevel.High,
    'bg-yellow-500/10': box.level === ScoreLevel.Medium,
    'bg-green-500/10': box.level === ScoreLevel.Low,
  }
}

watch(
  () => props.matches,
  () => {
    selectedNumber.value = null
  }
)

const markerRefs = new Map<number, HTMLElement>()

const setMarkerRef = (
  number: number,
  el: Element | ComponentPublicInstance | null
) => {
  if (!el) {
    markerRefs.delete(number)
    return
  }
  const node = (el as ComponentPublicInstance).$el ?? (el as HTMLElement)
  markerRefs.set(number, node as HTMLElement)
}

const fallbackMatch = (
  match: ImageMatchInput,
  number: number
): TimelineMatch => ({
  id: match.id,
  type: 'Image',
  level: match.level ?? ScoreLevel.High,
  title: 'Image',
  score: 0,
  number,
  location: match.location ?? '',
  markers: match.markers ?? [],
  registered: { start: 0, end: 0 },
  infringing: { start: 0, end: 0 },
})

const selectedMatch = computed<TimelineMatch | null>(() => {
  const number = selectedNumber.value
  if (number == null) return null
  const input = props.matches[number - 1]
  if (!input) return null
  const fromParent = props.timelineMatchById?.get(input.id)
  return fromParent ? { ...fromParent, number } : fallbackMatch(input, number)
})

const anchorRect = ref<{ x: number; top: number; bottom: number } | null>(null)

const TOOLTIP_W = 190
const TOOLTIP_H = 128
const TOOLTIP_GAP = 10

const updateAnchor = () => {
  const number = selectedNumber.value
  if (number == null || !imageRef.value) {
    anchorRect.value = null
    return
  }
  const box = boxes.value.find((b) => b.number === number)
  if (!box) {
    anchorRect.value = null
    return
  }
  const imgRect = imageRef.value.getBoundingClientRect()
  const cx = imgRect.left + ((box.left + box.width / 2) / 100) * imgRect.width
  const top = imgRect.top + (box.top / 100) * imgRect.height
  const bottom = imgRect.top + ((box.top + box.height) / 100) * imgRect.height
  anchorRect.value = { x: cx, top, bottom }
}

watch(
  selectedNumber,
  async () => {
    await nextTick()
    updateAnchor()
  },
  { immediate: true }
)

const onScroll = () => updateAnchor()
const onResize = () => updateAnchor()

const onDocumentClick = () => {
  if (selectedNumber.value == null) return
  selectedNumber.value = null
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, true)
  window.addEventListener('resize', onResize)
  document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll, true)
  window.removeEventListener('resize', onResize)
  document.removeEventListener('click', onDocumentClick)
})

const tooltipStyle = computed(() => {
  const a = anchorRect.value
  if (!a) return null
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768
  const padding = 8

  let top = a.top - TOOLTIP_H - TOOLTIP_GAP
  if (top < padding) top = a.bottom + TOOLTIP_GAP
  let left = a.x - TOOLTIP_W / 2
  left = Math.max(padding, Math.min(vw - TOOLTIP_W - padding, left))
  top = Math.max(padding, Math.min(vh - TOOLTIP_H - padding, top))

  return {
    position: 'fixed' as const,
    left: `${left}px`,
    top: `${top}px`,
    width: `${TOOLTIP_W}px`,
    height: `${TOOLTIP_H}px`,
    zIndex: 60,
  }
})

const onView = () => {
  const number = selectedNumber.value
  if (number == null) return
  const input = props.matches[number - 1]
  if (input) emit('view', input.id)
}
</script>
